import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  cn,
} from "@exhousing/design-system";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  ExternalLink,
  ImagePlus,
  Lock,
  PenLine,
  Receipt,
  RefreshCw,
  X,
} from "lucide-react";
import { useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import {
  useDeleteInspectionPhoto,
  useFinalizeInspection,
  useGenerateDeductions,
  useInspection,
  useInspectionPhotos,
  useSendInspectionForSigning,
  useUpdateInspection,
  useUpdateItems,
  useUploadInspectionPhoto,
} from "@/hooks/api/useInspections";

const CONDITIONS: { value: string; label: string; tone: string }[] = [
  { value: "new", label: "New", tone: "bg-success/15 text-success border-success/30" },
  { value: "good", label: "Good", tone: "bg-success/15 text-success border-success/30" },
  { value: "fair", label: "Fair", tone: "bg-warning/15 text-warning border-warning/30" },
  { value: "poor", label: "Poor", tone: "bg-danger/10 text-danger border-danger/30" },
  { value: "damaged", label: "Damaged", tone: "bg-danger/15 text-danger border-danger/40" },
];

const CONDITION_RANK: Record<string, number> = { new: 0, good: 1, fair: 2, poor: 3, damaged: 4 };
const condLabel = (v?: string | null) =>
  CONDITIONS.find((c) => c.value === v)?.label ?? v ?? "";
const isWorse = (cur?: string | null, before?: string | null) => {
  const c = cur ? CONDITION_RANK[cur] : undefined;
  const b = before ? CONDITION_RANK[before] : undefined;
  return c !== undefined && b !== undefined && c > b;
};

type Photo = import("@/types/api").components["schemas"]["PhotoResponse"];

function RoomPhotos({
  inspectionId,
  roomId,
  locked,
  beforePhotos,
}: {
  inspectionId: string;
  roomId: string;
  locked: boolean;
  beforePhotos?: Photo[];
}) {
  const { data: photos } = useInspectionPhotos(inspectionId);
  const upload = useUploadInspectionPhoto(inspectionId);
  const del = useDeleteInspectionPhoto(inspectionId);
  const inputRef = useRef<HTMLInputElement>(null);
  const roomPhotos = (photos ?? []).filter((p) => p.inspection_room_id === roomId);
  const hasBefore = (beforePhotos?.length ?? 0) > 0;

  const thumb = "size-16 rounded-md border border-border object-cover";

  return (
    <div className="mt-3 space-y-2">
      {hasBefore && (
        <div>
          <p className="mb-1 text-xs text-muted-foreground">At check-in</p>
          <div className="flex flex-wrap gap-2">
            {(beforePhotos ?? []).map((p) => (
              <img key={p.id} src={p.url} alt={p.caption ?? ""} className={cn(thumb, "opacity-90")} />
            ))}
          </div>
        </div>
      )}
      <div>
        {hasBefore && <p className="mb-1 text-xs text-muted-foreground">At check-out</p>}
        <div className="flex flex-wrap items-center gap-2">
          {roomPhotos.map((p) => (
            <div key={p.id} className="relative">
              <img src={p.url} alt={p.caption ?? ""} className={thumb} />
              {!locked && (
                <button
                  type="button"
                  aria-label="Delete photo"
                  onClick={() =>
                    del.mutate(p.id, { onError: (e) => toast.error(e.message) })
                  }
                  // Always visible — touch devices have no hover state.
                  className="absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full bg-danger text-white shadow ring-2 ring-background"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>
          ))}
          {!locked && (
            <>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="flex size-16 items-center justify-center rounded-md border border-dashed border-input text-muted-foreground hover:bg-muted"
              >
                <ImagePlus className="size-5" />
              </button>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file)
                    upload.mutate(
                      { file, roomId },
                      { onError: () => toast.error("Upload failed") },
                    );
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function InspectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: inspection, isLoading, isError } = useInspection(id);
  const updateItems = useUpdateItems(id ?? "");
  const updateInspection = useUpdateInspection(id ?? "");
  const finalize = useFinalizeInspection(id ?? "");
  const sendForSigning = useSendInspectionForSigning(id ?? "");
  const genDeductions = useGenerateDeductions(id ?? "");

  // A check-out is read against its check-in baseline (before/after). Hooks must run
  // unconditionally — an empty baselineId keeps the queries disabled.
  const baselineId = inspection?.baseline_inspection_id ?? "";
  const { data: baseline } = useInspection(baselineId);
  const { data: basePhotos } = useInspectionPhotos(baselineId, { enabled: !!baselineId });

  const baseCondById = useMemo(() => {
    const m = new Map<string, string>();
    baseline?.rooms.forEach((r) =>
      r.items.forEach((i) => i.condition && m.set(i.id, i.condition)),
    );
    return m;
  }, [baseline]);
  const basePhotosByRoom = useMemo(() => {
    const nameById = new Map<string, string>();
    baseline?.rooms.forEach((r) => nameById.set(r.id, r.name));
    const m = new Map<string, Photo[]>();
    (basePhotos ?? []).forEach((p) => {
      const name = p.inspection_room_id ? nameById.get(p.inspection_room_id) : undefined;
      if (!name) return;
      const arr = m.get(name) ?? [];
      arr.push(p);
      m.set(name, arr);
    });
    return m;
  }, [baseline, basePhotos]);

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (isError || !inspection)
    return <p className="text-sm text-danger">Inspection not found.</p>;

  const signed = inspection.status === "signed";
  // Once finalized the data is frozen (ready to sign) — edits stay locked through signing.
  const locked = inspection.status === "completed" || signed;
  const isCheckout = inspection.type === "check_out";

  return (
    <div className="space-y-6">
      <Link
        to="/inspections"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Inspections
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight capitalize">
            {inspection.type.replace(/_/g, " ")}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>{inspection.rooms.length} rooms</span>
            <span>·</span>
            <Link
              to={`/properties/${inspection.property_id}`}
              className="font-medium text-primary hover:underline"
            >
              Property →
            </Link>
            <span>·</span>
            <Link
              to={`/contracts/${inspection.contract_id}`}
              className="font-medium text-primary hover:underline"
            >
              Contract →
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={inspection.status} />
          {inspection.type === "check_out" && (
            <Button
              size="sm"
              variant="outline"
              disabled={genDeductions.isPending}
              onClick={() =>
                genDeductions.mutate(undefined, {
                  onSuccess: (r) =>
                    toast.success(`Generated ${r.created} deposit deduction(s)`),
                  onError: () =>
                    toast.error("Create a deposit on the contract first"),
                })
              }
            >
              <Receipt className="size-4" />
              Deposit deductions
            </Button>
          )}
        </div>
      </header>

      {/* Meters & keys */}
      <Card>
        <CardHeader>
          <CardTitle>Meters &amp; keys</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {(
            [
              ["meter_electricity", "Electricity"],
              ["meter_gas", "Gas"],
              ["meter_water", "Water"],
              ["keys_handed_over", "Keys"],
            ] as [string, string][]
          ).map(([field, label]) => (
            <div key={field} className="space-y-1.5">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                {label}
              </label>
              <Input
                type="number"
                defaultValue={
                  (inspection as Record<string, unknown>)[field] as number | undefined
                }
                disabled={locked}
                onBlur={(e) =>
                  e.target.value &&
                  updateInspection.mutate({ [field]: Number(e.target.value) })
                }
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Rooms */}
      {inspection.rooms.map((room) => (
        <Card key={room.id}>
          <CardHeader>
            <CardTitle>{room.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {room.items.map((item) => {
              const before =
                isCheckout && item.baseline_item_id
                  ? baseCondById.get(item.baseline_item_id)
                  : undefined;
              return (
                <div key={item.id} className="border-b border-border/60 pb-3 last:border-0">
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                    <span className="text-sm font-medium">{item.label}</span>
                    <div className="flex flex-wrap gap-1.5">
                      {CONDITIONS.map((c) => (
                        <button
                          key={c.value}
                          disabled={locked}
                          onClick={() =>
                            updateItems.mutate([
                              { item_id: item.id, condition: c.value },
                            ])
                          }
                          className={cn(
                            // Touch-friendly tap target on phones (inspections happen on-site).
                            "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                            item.condition === c.value
                              ? c.tone
                              : "border-border text-muted-foreground hover:bg-muted",
                          )}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {before && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Check-in: <span className="font-medium">{condLabel(before)}</span>
                      {isWorse(item.condition, before) && (
                        <span className="ml-1 font-medium text-danger">↓ worse</span>
                      )}
                    </p>
                  )}
                </div>
              );
            })}
            <RoomPhotos
              inspectionId={inspection.id}
              roomId={room.id}
              locked={locked}
              beforePhotos={isCheckout ? basePhotosByRoom.get(room.name) : undefined}
            />
          </CardContent>
        </Card>
      ))}

      {/* Signing */}
      <SigningPanel
        inspection={inspection}
        locked={locked}
        signed={signed}
        finalizing={finalize.isPending}
        sending={sendForSigning.isPending}
        onFinalize={() =>
          finalize.mutate(undefined, {
            onSuccess: () => toast.success("Report locked — ready to sign"),
            onError: (e) => toast.error(e.message),
          })
        }
        onSend={(resend) => {
          if (
            resend &&
            !window.confirm(
              "Resend will create a new signing link and email the signers again; the previous link stops working. Continue?",
            )
          )
            return;
          sendForSigning.mutate(undefined, {
            onSuccess: () =>
              toast.success(resend ? "Reminder sent" : "Sent for signing"),
            onError: (e) => toast.error(e.message),
          });
        }}
      />
    </div>
  );
}

function SigningPanel({
  inspection,
  locked,
  signed,
  finalizing,
  sending,
  onFinalize,
  onSend,
}: {
  inspection: { signing?: { signing_status?: string | null; signing_url?: string | null } | null };
  locked: boolean;
  signed: boolean;
  finalizing: boolean;
  sending: boolean;
  onFinalize: () => void;
  onSend: (resend: boolean) => void;
}) {
  const signing = inspection.signing ?? null;
  // Out for e-signature: any non-terminal signing state keeps us in the "awaiting" view.
  const sentOut =
    !!signing?.signing_status &&
    signing.signing_status !== "completed" &&
    signing.signing_status !== "rejected" &&
    !signed;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Signing</CardTitle>
        {signed ? (
          <Badge tone="success" dot>
            Signed
          </Badge>
        ) : sentOut ? (
          <Badge tone="warning" dot>
            Out for signature
          </Badge>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {signed ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="size-4 text-success" />
            The report is signed. A sealed copy is filed with the contract.
          </p>
        ) : sentOut ? (
          <>
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="size-4" />
              Waiting for the signer(s) to sign the report.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {signing?.signing_url && (
                <a
                  href={signing.signing_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                  Open signing page
                  <ExternalLink className="size-3.5" />
                </a>
              )}
              <Button size="sm" variant="ghost" disabled={sending} onClick={() => onSend(true)}>
                <RefreshCw className="size-4" />
                Resend
              </Button>
            </div>
          </>
        ) : locked ? (
          <>
            <p className="text-sm text-muted-foreground">
              The report is locked. Send it to the tenant (and landlord, if on file) to
              sign securely in their browser.
            </p>
            <Button size="sm" disabled={sending} onClick={() => onSend(false)}>
              <PenLine className="size-4" />
              Send for signing
            </Button>
          </>
        ) : (
          <>
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Lock className="size-4" />
              Finalize to lock the report, then send it out for signature.
            </p>
            <Button size="sm" variant="accent" disabled={finalizing} onClick={onFinalize}>
              <CheckCircle2 className="size-4" />
              Finalize
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
